/**
 * Renewing the session token — one implementation, every caller.
 *
 * The rule this exists to hold up (ID-8): a token that carries identity must be
 * re-derived from the session cookie, never minted as a successor to the token
 * it replaces. Renewing a token from itself lets a superseded identity refresh
 * indefinitely in a tab nobody reloaded, which is how one browser ends up
 * holding two live identities.
 *
 * `AppAuthProvider` was fixed to do that first. It was not the only thing
 * renewing a token: `ActivityTracker` (in this package) calls an app-supplied
 * `refreshToken`, and three apps implemented it as "POST /auth/refresh with the
 * current token as Bearer, write the result back to `{app}:authToken`" — the
 * exact shape ID-8 forbids, on the path that runs precisely when the provider's
 * own timer did not (a suspended tab). So the logic lives here and all four call
 * it.
 *
 * See apps/auth/docs/session-credential-standardization-plan.md (ID-8).
 */
import { getServiceApiUrl, getCsrfToken } from '@airclou/utils';
import { getImpersonationInfo, decodeJwtPayload } from '@airclou/auth';
// One decoder, one package. The copy that briefly lived here used bare `atob`
// on the base64url payload, so tokens carrying a non-ASCII name decoded to
// `null` — and every identity comparison built on it silently skipped.
export { decodeJwtPayload };
/**
 * Fired (on `window`) after a renewal derives a *different* identity or org
 * from the cookie and the caller has stored the adopted token. The provider
 * listens and re-fetches the user: without this, the ActivityTracker backstop
 * renewal swapped the token underneath a mounted app and nothing told the UI —
 * chrome kept showing the previous user while every API call answered as the
 * new one, and the focus-time reconcile could never heal it because the subs
 * already matched.
 */
export const IDENTITY_ADOPTED_EVENT = 'airclou:identity-adopted';
/**
 * Is this token a *live* impersonation session?
 *
 * `getImpersonationInfo` reports on shape, not on time — it returns info for an
 * impersonation token that expired an hour ago. That distinction matters
 * wherever the answer is used to skip an identity check: an impersonation token
 * names someone other than the cookie's user *by design*, but only while it is
 * live. Applied to a dead one, the carve-out suppresses the mismatch while the
 * expired token still forces a re-bootstrap — so the admin lands back on their
 * own identity holding the impersonated user's workspace selection, the exact
 * leak these checks exist to stop.
 */
export function isLiveImpersonation(token) {
    if (!token)
        return false;
    const info = getImpersonationInfo(token);
    return !!info && info.expiresAt > Math.floor(Date.now() / 1000);
}
/**
 * Drop scope state that belonged to a previous identity or org, so a new
 * identity cannot inherit the previous one's workspace selection.
 */
export function clearScopeKeys(appName, extraKeys = []) {
    localStorage.removeItem(`${appName}:workspaceId`);
    localStorage.removeItem(`${appName}:workspaceName`);
    for (const key of extraKeys) {
        localStorage.removeItem(key);
    }
}
/**
 * Every request here gets a deadline.
 *
 * These calls used to be fire-and-forget — a hang was invisible because nothing
 * waited on one. That stopped being true when `authServiceFetch` began awaiting
 * the reconcile before retrying a refused request: a stalled auth service now
 * stalls the caller, and the caller's own timeout does not cover the reconcile
 * it is waiting on. Fifteen seconds is well past a healthy round trip and well
 * short of a user deciding the page is broken.
 */
export const AUTH_REQUEST_TIMEOUT_MS = 15_000;
/** `undefined` where `AbortSignal.timeout` is missing, so the fetch still runs. */
export function authRequestSignal() {
    try {
        return AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS);
    }
    catch {
        return undefined;
    }
}
/** One POST to a renewal endpoint, reporting which kind of "no" it got. */
async function postRefresh(path, init) {
    try {
        const authApiUrl = getServiceApiUrl('auth');
        const response = await fetch(`${authApiUrl}${path}`, {
            ...init,
            signal: authRequestSignal(),
        });
        if (!response.ok) {
            // Only 401 is an answer about the credential. A 403 is usually
            // infrastructure (a WAF rule, a proxy, the CSRF guard on the cookie leg —
            // which answers with a bare `{ error, code }`), and treating it as "the
            // session is over" turns an infra refusal into a sign-out. But the refresh
            // handler itself answers 403 in one case — the token's workspace is no
            // longer the user's — and that one is deterministic: read as "no answer",
            // it was retried every minute for ever. Its body carries the service's own
            // `{ success: false, error: { code } }` envelope; the guards' do not.
            if (response.status === 403) {
                let body = null;
                try {
                    body = await response.json();
                }
                catch {
                    // Not JSON: a proxy page. Nothing was decided.
                }
                return { failure: body?.success === false && typeof body?.error?.code === 'string' ? 'forbidden' : 'unreachable' };
            }
            // Anything else — 5xx, a proxy error page — is the server failing to answer at all.
            if (response.status !== 401)
                return { failure: 'unreachable' };
            // An idle refusal is still a refusal, but the client acts on it
            // differently — it explains itself to the user instead of just bouncing
            // them to a login form. Read off the code, never the message.
            let code;
            try {
                code = (await response.json())?.error?.code;
            }
            catch {
                // A 401 with no parseable body is still an authoritative refusal.
            }
            // `SESSION_NOT_RENEWABLE` means this endpoint cannot renew *this shape* of
            // token — not that the session is over. The heavier `/auth/refresh` can (it
            // mints a `jti` and creates the row), so report it as a miss rather than a
            // refusal and let the caller re-derive. Treating it as `unauthenticated` would
            // sweep every app's credential over a token that was one call from healthy.
            if (code === 'SESSION_NOT_RENEWABLE')
                return { failure: 'not-renewable' };
            return { failure: code === 'SESSION_IDLE' ? 'idle' : 'unauthenticated' };
        }
        const data = await response.json();
        const payload = data.data ?? data;
        const token = payload?.token;
        return data.success && token
            ? { token, idleExpiresAt: payload.idleExpiresAt ?? null }
            : { failure: 'unreachable' };
    }
    catch {
        // Network error, timeout, malformed body: no answer.
        return { failure: 'unreachable' };
    }
}
/**
 * Mint a token from the session cookie — the one derivation that makes the
 * stored token a cache of the cookie rather than a second source of truth.
 * Sends no Authorization header on purpose: the cookie must be the credential.
 */
export async function exchangeCookieForToken() {
    // Bootstrap, so `/auth/refresh`: this is the one call that should re-derive
    // claims from Dashboard, because it is establishing who the session is rather
    // than keeping an established one alive.
    const attempt = await exchangeCookie('/auth/refresh');
    return 'token' in attempt ? attempt.token : null;
}
/** The same exchange, keeping the reason it failed. */
async function exchangeCookie(path, body) {
    const headers = { 'Content-Type': 'application/json' };
    const csrfToken = getCsrfToken();
    if (csrfToken)
        headers['x-csrf-token'] = csrfToken;
    return postRefresh(path, {
        method: 'POST',
        headers,
        credentials: 'include',
        ...(body !== undefined && { body: JSON.stringify(body) }),
    });
}
/**
 * Renew the session token, deriving from the cookie wherever one is reachable.
 *
 * Reports *which* kind of no it got rather than collapsing them: `impersonating`
 * means there was nothing to do (a live impersonation must never be re-derived
 * away), `unauthenticated` means the server refused the credential and clearing
 * it is correct, `unreachable` means we never got an answer and clearing it
 * would be a sign-out over a dropped connection. Callers store the returned
 * token themselves.
 */
export async function renewSessionToken({ currentToken, appName, extraKeysToClear = [], activity, activityAgeMs, }) {
    // An impersonation token names the impersonated user while the cookie still
    // names the admin who started it. That is a legitimate two-identity state, not
    // a stale one — never re-derive it away while it is live.
    if (isLiveImpersonation(currentToken)) {
        console.log(`[${appName}] Impersonating — leaving the session token as-is`);
        return { status: 'impersonating' };
    }
    const previousClaims = currentToken ? decodeJwtPayload(currentToken) : null;
    const previousSub = previousClaims?.sub ?? null;
    const previousTenantId = previousClaims?.tenantId ?? null;
    const usesActivity = activity !== undefined;
    const path = usesActivity ? '/auth/session/activity' : '/auth/refresh';
    const body = usesActivity
        ? { activity, ...(activity && activityAgeMs !== undefined && { activityAgeMs }) }
        : undefined;
    let attempt = await exchangeCookie(path, body);
    if (!('token' in attempt) && attempt.failure === 'not-renewable' && currentToken) {
        // This endpoint cannot renew this *shape* of token, which is not a statement
        // about the session — `/auth/refresh` heals it by minting a `jti`. So try the
        // heavier path on both legs, and then stop: an earlier version fell through to
        // the retry below, which re-asked the *original* path, got `not-renewable`
        // again, and overwrote the authoritative `unauthenticated` the heavier path had
        // just returned. The caller then saw `unreachable`, re-armed a 30-second timer,
        // and a genuinely dead session looped for ever without ever signing the user out.
        attempt = await exchangeCookie('/auth/refresh');
        if (!('token' in attempt) && attempt.failure !== 'idle' && attempt.failure !== 'forbidden') {
            attempt = await postRefresh('/auth/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
            });
        }
    }
    else if (!('token' in attempt) && attempt.failure !== 'idle' && attempt.failure !== 'forbidden' && currentToken) {
        // A refusal on the cookie leg is not an answer about the *session* — it is
        // usually just "this client has no cookie" (native shell, cookies blocked,
        // cross-site restrictions). The Bearer fallback is what decides. An idle
        // refusal is the exception: that one *is* about the session, and the cookie and
        // the Bearer name the same one, so retrying would only ask the same question.
        // A forbidden one likewise: the claims are the token's own, on either leg.
        attempt = await postRefresh(path, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${currentToken}`,
            },
            ...(body !== undefined && { body: JSON.stringify(body) }),
        });
        // The Bearer leg can report `not-renewable` too — a client with no reachable
        // cookie is exactly where a jti-less signup token ends up. Healing only the
        // cookie leg's answer left those tabs retrying every thirty seconds for ever,
        // never reaching the one endpoint that would mint them a `jti`.
        if (!('token' in attempt) && attempt.failure === 'not-renewable') {
            attempt = await postRefresh('/auth/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${currentToken}` },
            });
        }
    }
    if (!('token' in attempt)) {
        if (attempt.failure === 'idle') {
            console.warn(`[${appName}] Token refresh refused — the session went idle`);
        }
        else if (attempt.failure === 'not-renewable') {
            // Both endpoints declined this token's shape. Nothing left to try, but it is
            // still not evidence the session ended — leave the credential alone.
            console.warn(`[${appName}] Token could not be renewed by either endpoint`);
            return { status: 'unreachable' };
        }
        else if (attempt.failure === 'forbidden') {
            console.warn(`[${appName}] Token refresh refused — the server declined to re-derive this token's claims`);
        }
        else if (attempt.failure === 'unauthenticated') {
            console.warn(`[${appName}] Token refresh refused — the session is over`);
        }
        else {
            console.warn(`[${appName}] Token refresh could not reach the auth service`);
        }
        return { status: attempt.failure };
    }
    const newToken = attempt.token;
    // The cookie may name someone else — that is the whole point of asking it.
    // Drop scope state so the new identity cannot inherit the old one's.
    const newClaims = decodeJwtPayload(newToken);
    const newSub = newClaims?.sub ?? null;
    const newTenantId = newClaims?.tenantId ?? null;
    const identityChanged = !!previousSub && !!newSub && previousSub !== newSub;
    // Renewing from the cookie can also land on a different org, which the old
    // token-to-token renewal could never do: the previous code carried tenantId
    // forward, so workspace scope could not go stale this way.
    const tenantChanged = !!previousTenantId && !!newTenantId && previousTenantId !== newTenantId;
    if (identityChanged || tenantChanged) {
        console.log(`[${appName}] Session cookie differs from the stored token — adopting the cookie's ${identityChanged ? 'identity' : 'organization'}`);
        clearScopeKeys(appName, extraKeysToClear);
        // Tell the mounted app to re-fetch the user it is displaying. On a macrotask
        // deliberately: callers store the returned token *after* this resolves
        // (ActivityTracker's `setToken` runs synchronously once its await settles),
        // so by the time this fires, listeners read the adopted token.
        if (typeof window !== 'undefined') {
            setTimeout(() => {
                try {
                    window.dispatchEvent(new Event(IDENTITY_ADOPTED_EVENT));
                }
                catch {
                    // Non-DOM environment (tests): the renewal itself already succeeded.
                }
            }, 0);
        }
    }
    console.log(`[${appName}] Token refreshed successfully`);
    return { status: 'renewed', token: newToken, idleExpiresAt: attempt.idleExpiresAt };
}
